import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import CollectionsRoundedIcon from "@mui/icons-material/CollectionsRounded";
import GalleryFormDialog from "./GalleryFormDialog";
import GalleryImagesPanel from "./GalleryImagesPanel";
import { useApp } from "../../../context/AppContext";
import { useNavigate } from "react-router-dom";

export default function GalleryManager() {
  const navigate = useNavigate();
  const { clearSession } = useApp();

  const [galleries, setGalleries] = useState([]);
  const [selectedGalleryId, setSelectedGalleryId] = useState("");
  const [images, setImages] = useState([]);

  const [loadingGalleries, setLoadingGalleries] = useState(true);
  const [loadingImages, setLoadingImages] = useState(false);
  const [savingGallery, setSavingGallery] = useState(false);
  const [deletingGallery, setDeletingGallery] = useState(false);

  const [galleryDialogOpen, setGalleryDialogOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [message, setMessage] = useState({ type: "", text: "" });

  const request = useCallback(
    async (url, options = {}) => {
      const access = localStorage.getItem("access");

      const headers = {
        ...(options.body instanceof FormData
          ? {}
          : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
      };

      if (access) {
        headers.Authorization = `Bearer ${access}`;
      }

      const response = await fetch(url, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        clearSession();
        navigate("/admin-login", { replace: true });
        throw new Error("Tu sesión expiró");
      }

      const contentType = response.headers.get("content-type") || "";
      const data = contentType.includes("application/json")
        ? await response.json()
        : null;

      if (!response.ok) {
        throw new Error(
          data?.error ||
            data?.detail ||
            "Ocurrió un error al procesar la solicitud"
        );
      }

      return data;
    },
    [clearSession, navigate]
  );

  const loadGalleries = useCallback(
    async (preferredGalleryId = null) => {
      setLoadingGalleries(true);

      try {
        const data = await request("/gallery/admin/list/");
        const list = Array.isArray(data) ? data : [];

        setGalleries(list);

        if (!list.length) {
          setSelectedGalleryId("");
          setImages([]);
          return;
        }

        const preferredExists = preferredGalleryId
          ? list.some((item) => String(item.id) === String(preferredGalleryId))
          : false;

        const currentExists = selectedGalleryId
          ? list.some((item) => String(item.id) === String(selectedGalleryId))
          : false;

        if (preferredExists) {
          setSelectedGalleryId(String(preferredGalleryId));
        } else if (currentExists) {
          setSelectedGalleryId(String(selectedGalleryId));
        } else {
          setSelectedGalleryId(String(list[0].id));
        }
      } catch (error) {
        setMessage({ type: "error", text: error.message });
      } finally {
        setLoadingGalleries(false);
      }
    },
    [request, selectedGalleryId]
  );

  const loadImages = useCallback(async () => {
    if (!selectedGalleryId) {
      setImages([]);
      return;
    }

    setLoadingImages(true);

    try {
      const data = await request(`/gallery/admin/images/${selectedGalleryId}/`);
      setImages(Array.isArray(data) ? data : []);
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setLoadingImages(false);
    }
  }, [request, selectedGalleryId]);

  useEffect(() => {
    loadGalleries();
  }, [loadGalleries]);

  useEffect(() => {
    loadImages();
  }, [loadImages]);

  const selectedGallery = useMemo(() => {
    return galleries.find(
      (gallery) => String(gallery.id) === String(selectedGalleryId)
    );
  }, [galleries, selectedGalleryId]);

  const openCreateDialog = () => {
    setEditingGallery(null);
    setGalleryDialogOpen(true);
  };

  const openEditDialog = () => {
    if (!selectedGallery) return;
    setEditingGallery(selectedGallery);
    setGalleryDialogOpen(true);
  };

  const closeGalleryDialog = () => {
    setGalleryDialogOpen(false);
    setEditingGallery(null);
  };

  const openDeleteDialog = () => {
    if (!selectedGallery || deletingGallery) return;
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    if (deletingGallery) return;
    setDeleteDialogOpen(false);
  };

  const handleSaveGallery = async (values) => {
    setSavingGallery(true);
    setMessage({ type: "", text: "" });

    try {
      if (editingGallery) {
        await request(`/gallery/admin/update/${editingGallery.id}/`, {
          method: "PUT",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Galería actualizada correctamente",
        });

        await loadGalleries(editingGallery.id);
      } else {
        const created = await request("/gallery/admin/create/", {
          method: "POST",
          body: JSON.stringify(values),
        });

        setMessage({
          type: "success",
          text: "Galería creada correctamente",
        });

        await loadGalleries(created?.id || null);
      }

      closeGalleryDialog();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setSavingGallery(false);
    }
  };

  const handleDeleteGallery = async () => {
    if (!selectedGallery) return;

    setDeletingGallery(true);
    setMessage({ type: "", text: "" });

    try {
      await request(`/gallery/admin/delete/${selectedGallery.id}/`, {
        method: "DELETE",
      });

      setMessage({
        type: "success",
        text: "Galería eliminada correctamente",
      });

      setDeleteDialogOpen(false);
      await loadGalleries();
    } catch (error) {
      setMessage({ type: "error", text: error.message });
    } finally {
      setDeletingGallery(false);
    }
  };

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Card
          elevation={0}
          sx={{
            width: "95%",
            borderRadius: "22px",
            border: "1px solid rgba(140,15,44,0.12)",
            overflow: "hidden",
          }}
        >
          <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
            <Stack spacing={2.2}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <Stack spacing={0.7}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="h4">Gestión de galerías</Typography>
                  </Stack>
                </Stack>

                <Button
                  variant="contained"
                  startIcon={<AddRoundedIcon />}
                  onClick={openCreateDialog}
                  sx={{
                    alignSelf: { xs: "stretch", md: "auto" },
                    minHeight: 44,
                    borderRadius: "12px",
                  }}
                >
                  Nueva galería
                </Button>
              </Stack>

              {message.text && (
                <Alert
                  severity={message.type === "success" ? "success" : "error"}
                  onClose={() => setMessage({ type: "", text: "" })}
                >
                  {message.text}
                </Alert>
              )}

              <Card
                elevation={0}
                sx={{
                  borderRadius: "18px",
                  border: "1px solid rgba(140,15,44,0.12)",
                  background:
                    "linear-gradient(180deg, rgba(140,15,44,0.04) 0%, rgba(140,15,44,0.015) 100%)",
                }}
              >
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Stack spacing={1.8}>
                    <Stack
                      direction={{ xs: "column", lg: "row" }}
                      spacing={1.2}
                      alignItems={{ xs: "stretch", lg: "center" }}
                    >
                      <TextField
                        select
                        fullWidth
                        label="Galería seleccionada"
                        value={selectedGalleryId}
                        onChange={(e) => setSelectedGalleryId(e.target.value)}
                        disabled={loadingGalleries || !galleries.length}
                      >
                        {galleries.map((gallery) => (
                          <MenuItem key={gallery.id} value={String(gallery.id)}>
                            {gallery.name}
                          </MenuItem>
                        ))}
                      </TextField>

                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1}
                        sx={{ minWidth: { lg: 360 } }}
                      >
                        <Button
                          variant="outlined"
                          startIcon={<EditRoundedIcon />}
                          disabled={!selectedGallery || deletingGallery}
                          onClick={openEditDialog}
                          sx={{
                            minHeight: 44,
                            borderRadius: "12px",
                            flex: 1,
                          }}
                        >
                          Editar
                        </Button>

                        <Button
                          variant="outlined"
                          startIcon={<DeleteRoundedIcon />}
                          disabled={!selectedGallery || deletingGallery}
                          onClick={openDeleteDialog}
                          sx={{
                            minHeight: 44,
                            borderRadius: "12px",
                            flex: 1,
                          }}
                        >
                          {deletingGallery ? "Eliminando..." : "Eliminar"}
                        </Button>
                      </Stack>
                    </Stack>

                    <Divider />

                    <Stack
                      direction={{ xs: "column", md: "row" }}
                      spacing={1.2}
                      justifyContent="space-between"
                      alignItems={{ xs: "flex-start", md: "center" }}
                    >
                      <Stack spacing={0.8}>
                        <Typography
                          sx={{
                            fontSize: "1rem",
                            fontWeight: 800,
                            color: "text.primary",
                          }}
                        >
                          {selectedGallery
                            ? selectedGallery.name
                            : "Selecciona una galería"}
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Chip
                            icon={<CollectionsRoundedIcon />}
                            label={
                              selectedGallery
                                ? `${images.length} imagen${
                                    images.length === 1 ? "" : "es"
                                  }`
                                : "Sin selección"
                            }
                            sx={{ fontWeight: 700 }}
                          />

                          {loadingImages && (
                            <Chip
                              icon={<CircularProgress size={16} />}
                              label="Cargando imágenes"
                              sx={{ fontWeight: 700 }}
                            />
                          )}
                        </Stack>
                      </Stack>

                      {!galleries.length && !loadingGalleries && (
                        <Typography
                          sx={{
                            fontSize: "0.92rem",
                            color: "text.secondary",
                            fontWeight: 600,
                          }}
                        >
                          Crea tu primera galería para comenzar.
                        </Typography>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <GalleryImagesPanel
                galleryId={selectedGalleryId}
                galleryName={selectedGallery?.name || ""}
                images={images}
                loading={loadingImages}
                reloadImages={loadImages}
                onMessageChange={setMessage}
                request={request}
              />
            </Stack>
          </CardContent>
        </Card>
      </Box>

      <GalleryFormDialog
        open={galleryDialogOpen}
        onClose={closeGalleryDialog}
        onSubmit={handleSaveGallery}
        initialData={editingGallery}
        loading={savingGallery}
      />

      <Dialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: "18px",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: "primary.main",
            color: "#fff",
            fontWeight: 800,
            fontSize: "1.02rem",
          }}
        >
          Eliminar galería
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={1.2}>
            <Typography
              sx={{
                fontSize: "0.95rem",
                color: "text.primary",
                fontWeight: 700,
              }}
            >
              ¿Deseas eliminar esta galería?
            </Typography>

            <Typography
              sx={{
                fontSize: "0.9rem",
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              {selectedGallery
                ? `Se eliminará la galería "${selectedGallery.name}" junto con todas sus imágenes.`
                : "Esta acción no se puede deshacer."}
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 2.4,
            gap: 1,
          }}
        >
          <Button
            onClick={closeDeleteDialog}
            variant="outlined"
            disabled={deletingGallery}
          >
            Cancelar
          </Button>

          <Button
            onClick={handleDeleteGallery}
            variant="contained"
            disabled={deletingGallery}
            sx={{
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            {deletingGallery ? "Eliminando..." : "Eliminar"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}